import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ProfileContext } from '../../contexts/profileContext';
import Button from '../common/button';
import AdminManager from '../teamMemberManager';
import LanguageDropdown from './languageDropdown';
import Notifications from './notification';
import Search from './search';
import ExportPopup from './exportPopup';
import { List, X, SignOut, Export } from '@phosphor-icons/react';

export default function Header() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const [profile] = useContext(ProfileContext);
  const [searchParams] = useSearchParams();
  const {
    masterOrganization: {
      languages = [],
      translationPrefix,
      customConfigurations: { colors } = {}
    } = {}
  } = profile;

  const [langOptions, setLangOptions] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState();
  const [isExportPopupOpen, setIsExportPopupOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/login');
  };

  const handleChange = async (e) => {
    setSelectedLanguage(e);

    try {
      localStorage.setItem('lang', e.value);
    } catch (error) {
      console.log('LS Unavailable');
    }

    i18n.changeLanguage(e.value);
  };

  useEffect(() => {
    if (languages.length > 0) {
      const langOptions = languages.map((lang) => {
        return { label: lang?.toUpperCase(), value: lang };
      });
      setLangOptions(langOptions);
      selectLanguage(languages);
    }
  }, [languages]);

  const selectLanguage = (masterLanguages) => {
    let selectedLang;
    const seachParamLang = searchParams.get('lang');
    if (masterLanguages.includes(seachParamLang)) {
      selectedLang = seachParamLang;
    }
    try {
      if (selectedLang) {
        localStorage.setItem('lang', selectedLang);
      } else {
        const locallyStoredLang = localStorage.getItem('lang');
        if (masterLanguages.includes(locallyStoredLang)) selectedLang = locallyStoredLang;
        else selectedLang = masterLanguages?.[0] ?? 'en';
      }

      setSelectedLanguage({
        label: selectedLang.toUpperCase(),
        value: selectedLang
      });
    } catch (error) {
      console.log('LS Unavailable');
      selectedLang = masterLanguages?.[0] ?? 'en';
    } finally {
      i18n.changeLanguage(selectedLang);
    }
  };

  return (
    <>
      <div
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          isScrolled ? 'shadow-lg bg-white/95 backdrop-blur-sm' : 'bg-white'
        }`}>
        <div
          className={`flex-shrink-0 border-b-4 border-opacity-70 transition-colors duration-300`}
          style={{
            borderColor: colors?.primary
          }}>
          <header className="px-4 lg:px-8 w-full">
            <div className="flex justify-between items-center h-16 lg:h-20">
              {/* left */}
              <div className="hidden md:flex items-center gap-4">
                <AdminManager />
                <Button.ButtonOutlined
                  className="ml-6 px-6 py-2 font-bold transition-all duration-200 hover:bg-gray-50"
                  onClick={() => setIsExportPopupOpen(true)}>
                  {t(`exportAllData`)}
                </Button.ButtonOutlined>
              </div>

              {/* Mobile: Logo/Brand or AdminManager */}
              <div className="md:hidden">
                <AdminManager />
              </div>

              {/* right side desktop */}
              <div className="hidden md:flex items-center space-x-6">
                <Notifications className="shrink-0" />
                <Search />
                {langOptions.length > 1 && (
                  <span className="ml-4">
                    <LanguageDropdown
                      value={selectedLanguage}
                      options={langOptions}
                      onChange={handleChange}
                    />
                  </span>
                )}
                <Button.ButtonFilled
                  className="ml-6 px-6 py-2 font-bold transition-all duration-200 hover:opacity-90"
                  onClick={handleLogout}>
                  {t(`logout`)}
                </Button.ButtonFilled>
              </div>

              {/* Mobile right side */}
              <div className="md:hidden flex items-center gap-3">
                <Notifications className="shrink-0" />
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="p-2 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-colors">
                  <List size={24} weight="bold" />
                </button>
              </div>
            </div>
          </header>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden fixed inset-0 z-50 transform transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/30 transition-opacity duration-300 ${
            isMobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        />

        {/* Menu content */}
        <div className="absolute right-0 h-full w-[85%] max-w-[400px] bg-white shadow-xl flex flex-col">
          <div className="flex justify-end items-center p-4 border-b">
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-colors">
              <X size={24} weight="bold" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="flex flex-col p-4 space-y-6">
              {/* Search */}
              <div className="w-full">
                <Search className="w-full" />
              </div>

              {/* Language Selection */}
              {langOptions.length > 1 && (
                <div className="pb-4 border-b w-full">
                  {/* <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('selectLanguage')}
                  </label> */}
                  <LanguageDropdown
                    value={selectedLanguage}
                    options={langOptions}
                    onChange={handleChange}
                  />
                </div>
              )}

              {/* Export Data Button */}
              <button
                onClick={() => {
                  setIsExportPopupOpen(true);
                  setIsMobileMenuOpen(false);
                }}
                className="flex items-center gap-2 w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                <Export size={24} weight="bold" />
                <span className="font-medium">{t('exportAllData')}</span>
              </button>

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 w-full px-4 py-3 text-left text-red-600 hover:bg-red-50 rounded-lg transition-colors mt-auto">
                <SignOut size={24} weight="bold" />
                <span className="font-medium">{t('logout')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Spacer for fixed header */}
      <div className="h-16 lg:h-20" />

      <ExportPopup isOpen={isExportPopupOpen} onClose={() => setIsExportPopupOpen(false)} />
    </>
  );
}
